import React from 'react';
import { Box, Stack } from '@mui/material';
import { QuickFactsCard } from './QuickFactsCard';
import { QuickLinksCard } from './QuickLinksCard';
import { CollaboratorsCard } from './CollaboratorsCard';
import type { Person } from '../../../../types';
import type { PersonPageState, PermissionFlags } from '../PersonPage.types';

export interface ModalHandlers {
  openPhotoUpload: () => void;
  openDocumentUpload: () => void;
  openNoteEditor: () => void;
  openMemoryEditor: () => void;
  openEventForm: () => void;
  openRelationshipBuilder: () => void;
  openCommentForm: () => void;
  openResearchEditor: () => void;
}

interface SidebarProps {
  person: Person;
  counts: PersonPageState['counts'];
  permissions: PermissionFlags;
  modalHandlers: ModalHandlers;
}

/**
 * Sidebar - Desktop-only sidebar with utility cards
 *
 * Features:
 * - Sticky positioning (top: 180px)
 * - Stack layout with consistent spacing
 * - Only visible on desktop (≥1280px) - handled by parent Grid
 *
 * Contains:
 * - QuickFactsCard: Key person information (birth, location, occupation)
 * - QuickLinksCard: Combined view/add shortcuts for all content types
 * - CollaboratorsCard: Recent contributors
 */
export const Sidebar: React.FC<SidebarProps> = ({
  person,
  counts,
  permissions,
  modalHandlers
}) => {
  return (
    <Box
      sx={{
        position: 'sticky',
        top: 180
      }}
    >
      <Stack spacing={2}>
        {/* Quick Facts Card */}
        <QuickFactsCard person={person} />

        {/* Quick Links Card (combines Quick Add + Shortcuts) */}
        <QuickLinksCard
          personId={person.id}
          counts={counts}
          permissions={permissions}
          modalHandlers={modalHandlers}
        />

        {/* Collaborators Card */}
        <CollaboratorsCard
          personId={person.id}
          permissions={permissions}
        />
      </Stack>
    </Box>
  );
};
